import React, { useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export function InquiryForm({ type = "contact", trigger, defaultProgram = "", title }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    parent_name: "", email: "", phone: "", child_name: "", child_age: "",
    program_interest: defaultProgram, preferred_date: "", message: "",
  });

  const titleMap = { contact: t.form.contact, tour: t.form.tour, enrollment: t.form.enroll, summer_camp: t.form.camp };
  const heading = title || titleMap[type] || t.form.contact;
  const showDate = type === "tour";
  const showChild = type !== "contact";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { type, ...form });
      setDone(true);
      toast.success(t.form.success);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setDone(false); setForm({ parent_name: "", email: "", phone: "", child_name: "", child_age: "", program_interest: defaultProgram, preferred_date: "", message: "" }); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 200); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid={`inquiry-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy">{heading}</DialogTitle>
          <DialogDescription>We'll respond within one business day.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center flex flex-col items-center gap-3" data-testid="inquiry-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-500" />
            <p className="text-lg font-medium text-navy">{t.form.success}</p>
            <Button onClick={() => setOpen(false)} className="rounded-full bg-navy hover:bg-navy-dark mt-2" data-testid="inquiry-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t.form.name} required><Input required value={form.parent_name} onChange={set("parent_name")} data-testid="inq-name" /></Field>
              <Field label={t.form.email} required><Input type="email" required value={form.email} onChange={set("email")} data-testid="inq-email" /></Field>
              <Field label={t.form.phone}><Input value={form.phone} onChange={set("phone")} data-testid="inq-phone" /></Field>
              {showChild && <Field label={t.form.childAge}><Input value={form.child_age} onChange={set("child_age")} data-testid="inq-childage" /></Field>}
              {showChild && <Field label={t.form.childName}><Input value={form.child_name} onChange={set("child_name")} data-testid="inq-childname" /></Field>}
              {showDate && <Field label={t.form.date}><Input type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="inq-date" /></Field>}
              <Field label={t.form.program}><Input value={form.program_interest} onChange={set("program_interest")} data-testid="inq-program" /></Field>
            </div>
            <Field label={t.form.message}><Textarea rows={3} value={form.message} onChange={set("message")} data-testid="inq-message" /></Field>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-coral hover:bg-coral-dark text-white h-11 text-base" data-testid="inquiry-submit-btn">
              {loading ? t.form.sending : t.form.submit}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-navy/80">{label}{required && <span className="text-coral"> *</span>}</Label>
      {children}
    </div>
  );
}
